import { ChevronLeft, ChevronRight, Edit3, Trash2 } from 'lucide-react';
import { CALENDAR_COLORS, TAGS } from '../constants';
import { useSwipe } from '../hooks/useSwipe';
import type { CalendarEvent } from '../types';
import { calendarCells, dateKey, eventContainsDate, eventRange, monthShift, todayParts } from '../utils';
import { EmptyState } from './EmptyState';

export interface DateParts { year: number; month: number; day: number }

interface Props {
  events: CalendarEvent[];
  search: string;
  visibleMonth: { year: number; month: number };
  selectedDate: DateParts;
  monthDirection: -1 | 1;
  onMonthChange: (value: { year: number; month: number }, direction: -1 | 1) => void;
  onDateChange: (value: DateParts) => void;
  onEdit: (event: CalendarEvent) => void;
  onDelete: (event: CalendarEvent) => void;
}

const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

export function CalendarView(props: Props) {
  const today = todayParts();
  const cells = calendarCells(props.visibleMonth.year, props.visibleMonth.month);
  const search = props.search.trim().toLocaleLowerCase();
  const activeEvents = props.events.filter((event) => event.isEnabled && (!search || `${event.title} ${event.note}`.toLocaleLowerCase().includes(search)));
  const selectedEvents = activeEvents.filter((event) => eventContainsDate(event, props.selectedDate.year, props.selectedDate.month, props.selectedDate.day));

  const shift = (delta: -1 | 1) => props.onMonthChange(monthShift(props.visibleMonth.year, props.visibleMonth.month, delta), delta);
  const swipe = useSwipe(() => shift(1), () => shift(-1), false, true);

  return (
    <section className="calendar-view">
      <div className="month-panel" {...swipe}>
        <header className="month-header">
          <button className="icon-button" type="button" onClick={() => shift(-1)} aria-label="上個月"><ChevronLeft size={23} /></button>
          <strong>{props.visibleMonth.year} 年 {props.visibleMonth.month} 月</strong>
          <button className="icon-button" type="button" onClick={() => shift(1)} aria-label="下個月"><ChevronRight size={23} /></button>
        </header>
        <div className="weekday-row" aria-hidden="true">
          {dayNames.map((day) => <span key={day}>{day}</span>)}
        </div>
        <div
          key={`${props.visibleMonth.year}-${props.visibleMonth.month}`}
          className={`calendar-grid month-enter-${props.monthDirection > 0 ? 'right' : 'left'}`}
        >
          {cells.map((cell) => {
            const selected = dateKey(cell.year, cell.month, cell.day) === dateKey(props.selectedDate.year, props.selectedDate.month, props.selectedDate.day);
            const isToday = dateKey(cell.year, cell.month, cell.day) === dateKey(today.year, today.month, today.day);
            const currentMonth = cell.month === props.visibleMonth.month;
            const dayEvents = activeEvents.filter((event) => eventContainsDate(event, cell.year, cell.month, cell.day));
            return (
              <button
                type="button"
                key={`${cell.year}-${cell.month}-${cell.day}`}
                className={`${selected ? 'selected' : ''} ${isToday ? 'today' : ''} ${currentMonth ? '' : 'outside'}`}
                onClick={() => props.onDateChange(cell)}
                aria-label={`${cell.year}年${cell.month}月${cell.day}日`}
                aria-pressed={selected}
              >
                <span>{cell.day}</span>
                <span className="event-dots">
                  {dayEvents.slice(0, 3).map((event) => {
                    const color = CALENDAR_COLORS.find((candidate) => candidate.value === event.color)?.color;
                    return <i key={event.id} style={{ backgroundColor: color }} />;
                  })}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="selected-date-heading">
        <strong>{props.selectedDate.month} 月 {props.selectedDate.day} 日</strong>
        <span>{selectedEvents.length} 個事件</span>
      </div>

      {!selectedEvents.length ? (
        <EmptyState title="這天沒有事件" />
      ) : (
        <div className="calendar-events">
          {selectedEvents.map((event) => {
            const color = CALENDAR_COLORS.find((candidate) => candidate.value === event.color) ?? CALENDAR_COLORS[0];
            const tag = TAGS.find((candidate) => candidate.value === event.tag) ?? TAGS[0];
            return (
              <article className="item-card calendar-event-card" key={event.id}>
                <span className="card-accent" style={{ backgroundColor: color.color }} />
                <button className="card-main" type="button" onClick={() => props.onEdit(event)}>
                  <strong>{event.title}</strong>
                  <span className="event-range">{eventRange(event)}</span>
                  {event.note && <span className="card-note">{event.note}</span>}
                  <span className="card-meta" style={{ color: tag.color }}>{tag.label}</span>
                </button>
                <div className="mini-actions event-actions">
                  <button type="button" onClick={() => props.onEdit(event)} aria-label="編輯"><Edit3 size={17} /></button>
                  <button className="danger-icon" type="button" onClick={() => props.onDelete(event)} aria-label="刪除"><Trash2 size={17} /></button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
